import type { FeedbackDiagnosticsV1 } from '@kiao-voice/api-client/types/transcription'
import type { CreateTranscriptionResponse } from '@kiao-voice/api-client/types/transcription'
import type { EnhancementMode } from '@kiao-voice/core'

/**
 * Client for the voice-input streaming relay (the `voice:relay` ace command).
 *
 * Lifecycle, driven by the recording flow:
 *   start()      → open WS, send {type:'start'}, resolve on {type:'ready'}
 *   pushFrame()  → forward each base64 PCM frame (from the Rust `audio-pcm-frame`
 *                  event) as a binary WS frame; frames arriving before `ready`
 *                  are buffered, then flushed
 *   stop()       → send {type:'stop'}, resolve with the enhanced final text
 *
 * Any failure rejects the in-flight start()/stop() so the caller can fall back
 * to the batch path (the WAV is still written during recording). Transport seam
 * (`createWebSocket`) is injectable for tests.
 */

const WS_OPEN = 1
const BYTES_PER_SAMPLE = 2
// If the relay never returns a final after stop (wedged relay, half-open socket,
// stuck enhancement), reject so the caller falls back to the batch path.
//
// This is the OUTERMOST fuse of a layered stop budget:
//   - 30s (here): the only bound for legacy connections, connections that do not
//     advertise `stop_ack_v1`, and sessions that survived a mid-stream reconnect
//     (their upstream STT finalize can legitimately run long).
//   - 3s STOP_ACK_BAIL_MS + 15s ACKED_FINALIZE_TIMEOUT_MS: the inner, ack-governed
//     tiers, active only on the desktop's midstreamReconnect sessions.
//
// KEEP IN SYNC with the iOS transcribing watchdog:
//   apps/ios/kiao-voice-bridge/Sources/KiaoVoiceBridge/kiao-voice-bridge.swift
//   (KiaoVoiceStaleCapturePolicy.transcribingLimit, default of
//   VoiceSessionService.transcribeTimeoutDuration).
// Both bound the SAME outer wait — "user stopped, awaiting the relay's final" —
// so they must not drift, or the two platforms recover from a wedged relay on
// different clocks. The inner ack tiers are desktop-only and have no iOS twin.
const STOP_TIMEOUT_MS = 30_000
// If the socket reaches OPEN but the relay never emits `ready`, reject start() so
// the caller falls back to batch instead of spinning forever. A bad/half-open
// network is the motivating case: TCP connects, the start frame goes out, but no
// `ready`, `error`, or `close` ever arrives (a half-open TCP has no FIN/RST), so
// the connect-retry path — which only reacts to `error`/`close` — can't recover
// it. This deadline is armed once in start() and spans any connect retries (the
// total budget to reach `ready`). 15s covers the relay's ~3s pre-`ready` audio
// buffer + a WS handshake + network with wide margin, while still well under the
// 30s stop timer so a tap-off on a wedged connection falls back promptly.
const START_TIMEOUT_MS = 15_000
// No connect retry by default — existing callers keep today's "one shot, else
// fall back to batch" behavior. The desktop opts in (see maxConnectAttempts).
const DEFAULT_MAX_CONNECT_ATTEMPTS = 1
const DEFAULT_RECONNECT_DELAY_MS = 250
// This is deliberately telemetry-only. It gives us an early marker that the
// client→ingress→relay stop path may be wedged without changing the established
// 30-second fallback behavior while we gather production evidence.
const STOP_ACK_DELAY_MS = 2_000

/* Mid-stream reconnect constants. Values come from the resilience proposal v5
   (docs/voice-relay-resilience-proposal.md); the reasoning is inlined here so a
   reader of this file never has to go find the document. */
// 16 kHz s16le mono × 20 minutes. Past this the retained audio costs more memory
// than a reconnect is worth, so the session gives up its replay ability instead
// of growing without bound — the local WAV still holds the complete recording.
const MAX_FRAME_LOG_BYTES = 38_400_000
// Replay speed relative to realtime. Must stay comfortably above the Soniox
// engine's 1.1x ingest floor (below it the upstream treats the stream as
// realtime and the replay never catches up), and low enough that a 20-minute
// backlog does not arrive as one burst.
const REPLAY_PACE = 2
// Pause feeding the replay pump above this socket backlog so a slow uplink is
// not handed the whole log at once.
const MAX_REPLAY_BUFFERED_BYTES = 1_048_576
// How long a paused (backlogged) replay pump idles before re-checking. Fixed
// rather than derived from the frame it is holding: the frame has not been sent,
// so its duration says nothing about when the socket will have drained, and a
// zero-length one would spin the pump.
const REPLAY_BACKPRESSURE_IDLE_MS = 20
// Per-attempt backoff; anything past the table repeats the last value. There is
// deliberately NO attempt cap while recording — the user is still speaking, so
// giving up early would throw away a dictation that a later attempt could save.
// Termination comes from the stop-time recovery budget, a frame-log overflow, or
// abort(), never from an attempt count.
const MIDSTREAM_RECONNECT_BACKOFF_MS = [0, 500, 1_000, 2_000, 4_000] as const
const MIDSTREAM_RECONNECT_MAX_BACKOFF_MS = 5_000
// Per-attempt deadline for a replacement connection to reach `ready`. A socket
// that opens but never emits ready, error, OR close (a half-open TCP has no
// FIN/RST — the same case START_TIMEOUT_MS exists for) would otherwise pin the
// session in `reconnecting` indefinitely, because the reconnect loop is driven
// only by error/close. On expiry the attempt is abandoned to the next backoff.
const MIDSTREAM_ATTEMPT_READY_TIMEOUT_MS = 10_000
// Once the user has stopped, a reconnect is only worth waiting for this long —
// past it the batch path (which holds the full WAV) is the faster answer.
const STOP_RECOVERY_BUDGET_MS = 5_000
// A relay that supports `stop_ack_v1` acknowledges the stop almost immediately.
// No ack in this window means the client→ingress→relay stop path is wedged, and
// no amount of further waiting fixes it.
const STOP_ACK_BAIL_MS = 3_000
// The bail above measures from the local send(), but the stop frame queues
// BEHIND whatever PCM is still in the socket buffer — up to
// MAX_REPLAY_BUFFERED_BYTES right after a replay, and routinely nonzero on a
// degraded uplink during an ordinary dictation. A backlog over this threshold
// can plausibly take longer than STOP_ACK_BAIL_MS to drain, so the bail is not
// armed at all and the wait falls to the acked-15s / outer-30s tiers. 64 KiB of
// 16 kHz s16le mono is ~2 s of audio, comfortably under the 3 s bail even on an
// uplink running at realtime.
const STOP_ACK_BAIL_MAX_BUFFERED_BYTES = 65_536
// Acked, so the relay HAS the stop: bound the finalize itself. Not applied to a
// session that survived a mid-stream reconnect — that one just replayed its
// backlog upstream and can legitimately need longer than this, so it keeps only
// the outer 30s fuse.
const ACKED_FINALIZE_TIMEOUT_MS = 15_000

const CLIENT_SESSION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export type VoiceStreamingTransportEventType =
  | 'socket_created'
  | 'socket_opened'
  | 'ready'
  | 'connect_retry'
  | 'stop_sent'
  | 'stop_ack_received'
  | 'stop_ack_delayed'
  | 'final_received'
  | 'socket_error'
  | 'socket_closed'
  | 'start_timeout'
  | 'final_timeout'
  | 'midstream_reconnect_attempt'
  | 'midstream_reconnect_ready'
  | 'replay_completed'
  | 'stop_ack_timeout'
  // The relay has taken all the audio the plan's per-句 cap allows and stopped
  // forwarding the rest upstream. Not a failure: what it already heard still
  // comes back as a transcript.
  | 'cap_reached'
  // The stop-time recovery budget ran out mid-reconnect and the dictation went to
  // batch. Without it this path leaves NO native-log trace at all — the real-device
  // run that found the throttled pump had to infer it from the relay's own logs.
  | 'stop_recovery_timeout'

export interface VoiceStreamingTransportSnapshot {
  clientSessionId: string
  elapsedMs: number
  connectAttempt: number
  readyState: number | null
  /**
   * Frames physically sent across ALL of this session's sockets, replayed ones
   * included — so a reconnect makes this exceed the number of distinct frames
   * the user actually spoke.
   */
  framesSent: number
  /**
   * Bytes physically sent across ALL sockets, replays included. For forensic
   * reconciliation: live-only bytes = `bytesSent - replayedBytes`.
   */
  bytesSent: number
  bufferedAmount: number
  maxBufferedAmount: number
  pendingFrames: number
  /**
   * Mid-stream (post-`ready`) reconnect attempts started by this session,
   * accumulated over its whole lifetime across every separate outage. The
   * backoff schedule uses a per-outage index instead, so this number does NOT
   * indicate how long the current attempt waited.
   */
  midstreamReconnectAttempts: number
  /** Bytes re-sent by the replay pump, summed over all reconnects. */
  replayedBytes: number
  socketCreatedElapsedMs?: number
  socketOpenedElapsedMs?: number
  readyElapsedMs?: number
  lastInboundElapsedMs?: number
  lastPartialElapsedMs?: number
  stopSentElapsedMs?: number
  stopAckElapsedMs?: number
  stopAckLatencyMs?: number
  finalElapsedMs?: number
  closeCode?: number
  closeReason?: string
  closeWasClean?: boolean
}

export interface VoiceStreamingTransportEvent {
  type: VoiceStreamingTransportEventType
  occurredAtMs: number
  snapshot: VoiceStreamingTransportSnapshot
}

/**
 * A failure the relay reported in an `error` frame. `message` keeps the
 * frame's `message` (what every shipped client compared against); `code` is
 * the frame's structured `code` when it carries one, else null. A caller
 * branches on `code` to tell a policy refusal (for example
 * `team_member_disabled`, where falling back to another transport or
 * workspace is not allowed) apart from a transport failure it may recover from.
 */
export class VoiceRelayError extends Error {
  readonly code: string | null

  constructor(message: string, code: string | null) {
    super(message)
    this.name = 'VoiceRelayError'
    this.code = code
  }
}

/** The relay's structured `code` behind a thrown value, or null. */
export function voiceRelayErrorCode(cause: unknown): string | null {
  return cause instanceof VoiceRelayError ? cause.code : null
}

/** Stable client-side failure messages consumed by desktop fallback telemetry. */
export const VOICE_STREAMING_FAILURE_MESSAGES = {
  readyTimeout: 'voice relay ready timed out',
  stopTimeout: 'voice relay stop timed out',
  connectionError: 'voice relay connection error',
  closedBeforeCompleting: 'voice relay closed before completing',
  invalidMessage: 'voice relay sent an invalid message',
  relayClosedMidRecording: 'voice relay closed mid recording',
  stopAckTimeout: 'voice relay stop ack timed out',
} as const

export interface StreamingStartContext {
  bundleId?: string
  websiteDomain?: string
  /**
   * Text sitting immediately BEFORE the cursor in the focused field at
   * recording start. The relay forwards it to the STT engine as free-form
   * recognition context, so dictating mid-document biases toward the
   * surrounding wording (homophone/terminology disambiguation) — the STT-side
   * sibling of the enhancement-side `contextBefore` sent at stop.
   */
  contextText?: string
}

interface WebSocketLike {
  readyState: number
  bufferedAmount?: number
  send(data: string | Uint8Array): void
  close(code?: number, reason?: string): void
  addEventListener(type: string, listener: (event: WebSocketEventLike) => void): void
}

interface WebSocketEventLike {
  data?: unknown
  code?: number
  reason?: string
  wasClean?: boolean
}

type WebSocketFactory = (url: string) => WebSocketLike

export interface VoiceStreamingSessionOptions {
  relayUrl: string
  sampleRate: number
  language?: string
  /**
   * Frontmost-app context at recording start. The relay resolves the matched
   * power profile and compiles the STT context terms server-side from these
   * (the SAME resolver + compiler the async voice endpoint uses), so both
   * modes select identical dictionaries. The capture resolves only after the
   * session is constructed, so the function form is awaited right before the
   * start frame is sent — audio frames are buffered until `ready`, so nothing
   * is lost while it resolves. A failed resolver degrades to a start without
   * app context (→ the server resolves the default profile, like async).
   */
  startContext?: StreamingStartContext | (() => Promise<StreamingStartContext | undefined>)
  token?: string
  /**
   * The desktop's selected active context (`personal` / `org:<id>`) — the SAME
   * value the async path sends as the `X-Active-Context` header. Forwarded in
   * the `start` frame so the relay meters realtime voice against the right org
   * (a dictation in a team context counts toward the TEAM, not personal). Omit
   * (the user hasn't explicitly switched) → the server applies its smart default.
   */
  activeContext?: string
  onPartial?: (text: string) => void
  /**
   * The relay reached the per-句 duration cap and stopped forwarding audio
   * upstream; `maxDurationSecs` is the cap it applied (absent on a relay that
   * does not report it). Fires at most once per session.
   *
   * The dictation is still live and still finalizes normally — everything heard
   * up to the cap comes back from `stop()`. Callers should end the recording
   * here: further speech cannot reach the transcript, so leaving the recorder
   * running only produces a tail the user believes was captured. A caller that
   * ignores this still gets its transcript; it just keeps recording silence.
   */
  onCapReached?: (maxDurationSecs?: number) => void
  /** Anonymous per-dictation UUID used to correlate client, ingress, and relay logs. */
  clientSessionId?: string
  /** Receives content-free transport milestones suitable for durable diagnostics. */
  onTransportEvent?: (event: VoiceStreamingTransportEvent) => void
  createWebSocket?: WebSocketFactory
  /**
   * Transport-level connect resilience. The FIRST relay connection after the app
   * has been idle sometimes fails to reach the relay at all (TCP cold-start / the
   * relay momentarily busy — observed in prod as an async-fallback row with NO
   * relay session logged, immediately followed by a successful realtime dictation
   * seconds later). A single such blip otherwise dooms the whole dictation to the
   * slower async batch path. Set >1 to retry the CONNECT a bounded number of times
   * before giving up to batch.
   *
   * Only transport failures BEFORE `ready` are retried — a server-sent `error`
   * (auth/quota) or a drop AFTER `ready` is terminal: retrying the former is
   * pointless and retrying the latter would start a fresh upstream STT stream that
   * can't replay the already-flushed audio. PCM buffered before the failed attempt
   * is preserved across the retry, so no leading audio is clipped on recovery.
   * Defaults to 1 (no retry).
   */
  maxConnectAttempts?: number
  /** Delay between connect attempts in ms. Only used when maxConnectAttempts > 1. Default 250. */
  reconnectDelayMs?: number
  /**
   * Invoked before each RETRY (i.e. for attempt 2, 3, …) with the upcoming attempt
   * number and the failure that triggered it. These recoveries leave NO relay-side
   * log (the WS never reached the relay), so this is the only signal that a retry
   * saved a dictation from falling back to batch — wire it to client telemetry.
   */
  onReconnectAttempt?: (attempt: number, error: Error) => void
  /**
   * Mid-stream (POST-`ready`) reconnect. Off by default: a drop after `ready` is
   * then terminal and the caller falls back to batch.
   *
   * When on, the session retains every PCM frame it has seen and, after an
   * unexpected drop, opens a fresh relay connection under the SAME
   * clientSessionId and replays the whole recording at {@link REPLAY_PACE}×
   * realtime before resuming live — so a mid-sentence network blip costs the
   * user a moment of latency instead of the entire realtime transcript.
   *
   * OFF is not "no reconnect handling": a post-`ready` close with no promise in
   * flight fails the session with `relayClosedMidRecording` either way. The flag
   * chooses between recovering the dictation and ending it, never between ending
   * it and leaving it alive.
   *
   * Requires a relay that admits a second concurrent connection for the
   * credential. Anonymous trial tokens are capped at ONE concurrent dictation
   * server-side, so they must leave this off (see use-anonymous-dictation).
   */
  midstreamReconnect?: boolean
}

/**
 * The row a survey recording's `final` names (`vsq_` survey tokens only); the
 * page links it to the answer on its next save. Absent for every dictation.
 */
interface SurveyTranscriptionRef {
  transcriptionId?: number
}

/** 串流 transport 的內部結果；text 維持既有貼上用 trim 語意。 */
export type VoiceStreamingResult =
  | (Extract<CreateTranscriptionResponse, { status: 'ok' }> &
      SurveyTranscriptionRef & {
        /** 僅伺服器未保存且 trim 改變內容時補存原始 final；回饋取此值 ?? text。 */
        feedbackFinalText?: string
      })
  | (Extract<CreateTranscriptionResponse, { status: 'noise' }> & SurveyTranscriptionRef)

/**
 * Captured by the desktop at stop time (cursor surroundings + active power
 * profile) and sent in the `stop` message so server-side enhancement gets the
 * same context + dictionary glossary as the batch path.
 */
export interface VoiceStreamingStopOptions {
  contextBefore?: string
  contextAfter?: string
  dictionaryIds?: number[]
  appName?: string
  language?: string
  /** Enhancement mode resolved on the desktop. Omit to let the relay default to `faithful`. */
  enhancementMode?: EnhancementMode
}

export class VoiceStreamingSession {
  #options: VoiceStreamingSessionOptions
  #createWebSocket: WebSocketFactory
  #ws: WebSocketLike | null = null
  #ready = false
  // The relay has taken its fill of audio. Set once, never cleared: a replacement
  // connection after a reconnect replays the same recording and caps again, and
  // the caller must not be told twice about one cap.
  #capped = false
  #stopAckSupported = false
  #pending: Uint8Array[] = []
  #failure: Error | null = null
  #stopRequested = false
  #stopTimer: ReturnType<typeof setTimeout> | null = null
  #readyTimer: ReturnType<typeof setTimeout> | null = null
  #startResolve: (() => void) | null = null
  #startReject: ((error: Error) => void) | null = null
  #stopResolve: ((result: VoiceStreamingResult) => void) | null = null
  #stopReject: ((error: Error) => void) | null = null
  #stopOptions: VoiceStreamingStopOptions = {}
  #maxConnectAttempts: number
  #reconnectDelayMs: number
  #connectAttempts = 0
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null
  #ackDelayTimer: ReturnType<typeof setTimeout> | null = null
  #clientSessionId: string
  #sessionStartedAtMs = Date.now()
  #socketCreatedElapsedMs: number | undefined
  #socketOpenedElapsedMs: number | undefined
  #readyElapsedMs: number | undefined
  #lastInboundElapsedMs: number | undefined
  #lastPartialElapsedMs: number | undefined
  #stopSentElapsedMs: number | undefined
  #stopAckElapsedMs: number | undefined
  #finalElapsedMs: number | undefined
  #framesSent = 0
  #bytesSent = 0
  #bufferedAmount = 0
  #maxBufferedAmount = 0
  #stopSent = false
  #closeCode: number | undefined
  #closeReason: string | undefined
  #closeWasClean: boolean | undefined
  #midstreamReconnect: boolean
  // Every frame the session has accepted, kept so a fresh connection can be
  // brought up to date. Cleared by #fail (the audio lives on in the local WAV).
  #frameLog: Uint8Array[] = []
  #frameLogBytes = 0
  #frameLogOverflowed = false
  #reconnecting = false
  #replaying = false
  #everReconnected = false
  #completed = false
  // Lifetime total across the whole session, for telemetry.
  #midstreamAttempts = 0
  // Backoff index for the CURRENT outage only, reset once an attempt reaches
  // `ready`. Sharing the lifetime counter would make the first attempt of a
  // later, unrelated blip start at the 5s ceiling — and after a stop that alone
  // exhausts the 5s recovery budget, guaranteeing a batch fallback.
  #midstreamCycleAttempts = 0
  #replayIndex = 0
  #replayedBytes = 0
  // Monotonic wall-clock anchor for the replay pump. The pump paces itself
  // against this instead of against how often its timer fires, because the
  // desktop records with its main window hidden (menubar app) and macOS clamps
  // a hidden WKWebView's setTimeout to roughly one second — measured, and NOT
  // prevented by `backgroundThrottling: "disabled"` in tauri.conf.json.
  #replayStartedAtMs = 0
  // Wall-clock time the frames sent so far were entitled to consume, i.e. the
  // sum of #replayIntervalMs over them. Compared against the elapsed clock to
  // decide how many frames are due on each wakeup.
  #replayEmittedMs = 0
  // Time the pump spent parked on backpressure, subtracted from the elapsed
  // clock. A pause buys no audio, so counting it as debt would make the pump
  // fire the whole paused span's worth of frames the instant the socket drains
  // — the very burst the backlog ceiling exists to prevent.
  #replayPausedMs = 0
  #replayPauseStartedAtMs: number | null = null
  #midstreamTimer: ReturnType<typeof setTimeout> | null = null
  #midstreamReadyTimer: ReturnType<typeof setTimeout> | null = null
  #replayTimer: ReturnType<typeof setTimeout> | null = null
  #stopRecoveryTimer: ReturnType<typeof setTimeout> | null = null
  #stopAckBailTimer: ReturnType<typeof setTimeout> | null = null
  #ackedFinalizeTimer: ReturnType<typeof setTimeout> | null = null
  // Resolved once and reused by every reconnect handshake. Re-running the
  // resolver would capture the text the user has ALREADY dictated into the
  // field and feed it back to the STT engine as recognition context.
  #startContextResolved = false
  #cachedStartContext: StreamingStartContext | undefined

  constructor(options: VoiceStreamingSessionOptions) {
    this.#options = options
    this.#midstreamReconnect = options.midstreamReconnect ?? false
    this.#createWebSocket =
      options.createWebSocket ?? ((url) => new WebSocket(url) as unknown as WebSocketLike)
    this.#maxConnectAttempts = Math.max(
      1,
      options.maxConnectAttempts ?? DEFAULT_MAX_CONNECT_ATTEMPTS
    )
    this.#reconnectDelayMs = options.reconnectDelayMs ?? DEFAULT_RECONNECT_DELAY_MS
    this.#clientSessionId = resolveClientSessionId(options.clientSessionId)
  }

  start(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.#startResolve = resolve
      this.#startReject = reject
      // Arm the ready deadline once, spanning any connect retries. Cleared when
      // `ready` arrives (#onMessage) or the session fails (#fail). Without it a
      // socket that opens but never gets `ready` — and never errors/closes —
      // would leave start() pending forever (the spinning-forever bug).
      this.#readyTimer = setTimeout(() => {
        this.#emitTransportEvent('start_timeout')
        this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.readyTimeout))
      }, START_TIMEOUT_MS)
      this.#openSocket()
    })
  }

  // One PRE-`ready` connect attempt, drawn from the maxConnectAttempts budget.
  // The start()/stop() promises and any buffered PCM live on the instance, so a
  // fresh socket transparently resumes the same dictation.
  #openSocket(): void {
    this.#connectAttempts += 1
    if (this.#connectAttempts > 1) {
      this.#emitTransportEvent('connect_retry')
    }
    this.#createAndBindSocket()
  }

  // Socket construction + listener wiring, shared by the pre-`ready` connect
  // retry and the mid-stream reconnect. Deliberately does NOT touch
  // #connectAttempts: that budget belongs to reaching `ready` the first time,
  // and a mid-stream reconnect keeps its own (uncapped-while-recording) count.
  #createAndBindSocket(): void {
    // Captured HERE, not read at handshake time: this attempt's nature is fixed
    // when the socket is created, and #reconnecting is cleared the moment the
    // replacement reaches `ready`. Only a mid-stream reconnect resumes a dictation
    // the relay already started — a pre-`ready` connect retry does not, so it must
    // not claim to.
    const reconnectAttempt = this.#reconnecting
    let socket: WebSocketLike
    try {
      socket = this.#createWebSocket(
        addClientSessionIdToRelayUrl(this.#options.relayUrl, this.#clientSessionId)
      )
    } catch (error) {
      const failure =
        error instanceof Error ? error : new Error('failed to open voice relay socket')
      if (this.#reconnecting) this.#onMidstreamAttemptFailure()
      else this.#onConnectFailure(failure)
      return
    }
    this.#ws = socket
    this.#socketCreatedElapsedMs = this.#elapsedMs()
    this.#emitTransportEvent('socket_created')

    // Every listener is guarded by socket identity so a superseded attempt's late
    // events (a retried socket's old error/close firing after we moved on) can't
    // corrupt the live one.
    socket.addEventListener('open', () => {
      if (this.#ws !== socket) return
      this.#socketOpenedElapsedMs = this.#elapsedMs()
      this.#emitTransportEvent('socket_opened')
      void this.#sendStart(socket, reconnectAttempt)
    })
    socket.addEventListener('message', (event) => {
      if (this.#ws !== socket) return
      this.#onMessage(event)
    })
    socket.addEventListener('error', () => {
      if (this.#ws !== socket) return
      this.#emitTransportEvent('socket_error')
      // A failed mid-stream reconnect attempt: back off and try again.
      if (this.#reconnecting) {
        this.#onMidstreamAttemptFailure()
        return
      }
      // Before `ready` this is a failed connect, retryable from the connect budget.
      if (!this.#ready) {
        this.#onConnectFailure(new Error(VOICE_STREAMING_FAILURE_MESSAGES.connectionError))
        return
      }
      // After `ready`: a mid-stream drop. Recoverable only when the frame log can
      // still replay the recording onto a fresh upstream STT stream.
      if (this.#canMidstreamReconnect()) this.#beginMidstreamReconnect()
      else this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.connectionError))
    })
    socket.addEventListener('close', (event) => {
      if (this.#ws !== socket) return
      this.#onClose(event)
    })
  }

  // Synchronous for a plain-object startContext AND for every reconnect
  // handshake (both read an already-settled value, so no await runs before the
  // send) — the legacy timing is preserved; only the FIRST resolution of the
  // lazy function form defers the frame.
  async #sendStart(socket: WebSocketLike, reconnectAttempt = false): Promise<void> {
    const source = this.#options.startContext
    if (!this.#startContextResolved) {
      if (typeof source === 'function') {
        try {
          this.#cachedStartContext = await source()
        } catch (error) {
          // Visible recovery: the dictation proceeds without app context (the
          // server resolves the default profile's dictionaries instead).
          console.warn('voice relay: start context resolution failed; starting without it', error)
          this.#cachedStartContext = undefined
        }
      } else {
        this.#cachedStartContext = source
      }
      // Cached even when the resolver threw, so a reconnect does not re-run it:
      // by then the field holds the text the user has ALREADY dictated, and
      // feeding that back as STT recognition context biases the engine toward
      // repeating itself.
      this.#startContextResolved = true
    }
    const startContext = this.#cachedStartContext

    // The socket may have errored or been replaced while the context resolved.
    if (this.#failure || this.#ws !== socket || socket.readyState !== WS_OPEN) {
      return
    }

    socket.send(
      JSON.stringify({
        type: 'start',
        clientSessionId: this.#clientSessionId,
        // `cap_v1`: this client understands the `capped` frame. The relay only
        // sends it to a client that asked for it, because the shipped iOS and
        // Android clients tear the dictation down on an unknown inbound type.
        capabilities: ['stop_ack_v1', 'cap_v1'],
        sampleRate: this.#options.sampleRate,
        // Present ONLY on a mid-stream reconnect handshake. The relay may use it
        // to re-route the resumed session's STT (the recovery provider), so a
        // first connect and a pre-`ready` connect retry both omit it entirely.
        ...(reconnectAttempt ? { reconnect: true } : {}),
        ...(this.#options.language ? { language: this.#options.language } : {}),
        ...(startContext?.bundleId ? { bundleId: startContext.bundleId } : {}),
        ...(startContext?.websiteDomain ? { websiteDomain: startContext.websiteDomain } : {}),
        ...(startContext?.contextText ? { contextText: startContext.contextText } : {}),
        ...(this.#options.token ? { token: this.#options.token } : {}),
        ...(this.#options.activeContext ? { activeContext: this.#options.activeContext } : {}),
      })
    )
  }

  pushFrame(base64Pcm: string): void {
    // Past the cap the relay forwards nothing upstream, so this audio cannot
    // reach the transcript on ANY path — not live, and not through a replay
    // after a reconnect (the replacement connection caps at the same point).
    // Dropping it here keeps it out of the uplink and out of the frame log.
    if (this.#capped) return
    const bytes = base64ToBytes(base64Pcm)
    // An empty payload carries no audio and has no duration, so it can only add
    // a zero-length entry the replay pump would pace at 0 ms.
    if (bytes.byteLength === 0) return
    if (this.#midstreamReconnect) {
      // Terminal session: the complete recording is in the local WAV, which is
      // what the batch fallback uploads, so retaining more PCM buys nothing.
      if (this.#failure) return
      this.#appendToFrameLog(bytes)
      // Appending can itself end the session (an overflow mid-recovery), and a
      // terminated session must not start re-filling #pending.
      if (this.#failure) return
      // While recovering, the replay pump is the ONLY sender — routing the frame
      // anywhere else here would either duplicate it (replay + flushPending) or
      // deliver it ahead of the backlog it belongs behind.
      if (this.#reconnecting || this.#replaying) return
    }
    if (this.#ready && this.#ws && this.#ws.readyState === WS_OPEN) {
      this.#sendAudioFrame(this.#ws, bytes)
    } else {
      this.#pending.push(bytes)
    }
  }

  #appendToFrameLog(bytes: Uint8Array): void {
    if (this.#frameLogOverflowed) return
    if (this.#frameLogBytes + bytes.byteLength > MAX_FRAME_LOG_BYTES) {
      // Past the cap the log can no longer reconstruct the recording, so it is
      // dead weight — drop it and give up the reconnect ability rather than hold
      // tens of megabytes that will never be replayed.
      this.#frameLogOverflowed = true
      this.#frameLog = []
      this.#frameLogBytes = 0
      // Mid-recovery there is nothing left to replay onto the new connection, so
      // end it now instead of stalling until a timer notices.
      if (this.#reconnecting || this.#replaying) {
        this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.relayClosedMidRecording))
      }
      return
    }
    this.#frameLog.push(bytes)
    this.#frameLogBytes += bytes.byteLength
  }

  stop(options: VoiceStreamingStopOptions = {}): Promise<VoiceStreamingResult> {
    return new Promise((resolve, reject) => {
      if (this.#failure) {
        reject(this.#failure)
        return
      }
      this.#stopResolve = resolve
      this.#stopReject = reject
      this.#stopRequested = true
      this.#stopOptions = options
      // Defer the {stop} until the relay is ready: sending it before {ready}
      // (fast tap-on/tap-off, or while the socket is still CONNECTING) makes the
      // server's #handleStop no-op, so no final ever comes back. #sendStopIfReady
      // also runs from the ready handler.
      // Stopped mid-recovery: bound how long the reconnect gets before the batch
      // path (which already holds the full WAV) becomes the faster answer.
      this.#armStopRecoveryBudget()
      this.#sendStopIfReady()
      this.#stopTimer = setTimeout(() => {
        this.#emitTransportEvent('final_timeout')
        this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.stopTimeout))
      }, STOP_TIMEOUT_MS)
    })
  }

  // Armed both when stop() lands during a recovery AND when a healthy, already
  // stopped session drops into one (the stop was sent, then the socket died).
  #armStopRecoveryBudget(): void {
    if (!this.#midstreamReconnect || !this.#stopRequested) return
    if (this.#stopRecoveryTimer || this.#failure) return
    if (!this.#reconnecting && !this.#replaying) return
    this.#stopRecoveryTimer = setTimeout(() => {
      this.#stopRecoveryTimer = null
      // Emitted BEFORE #fail: that call tears the session down (sockets closed,
      // frame log dropped), so a snapshot taken afterwards would no longer show
      // the state the recovery gave up in.
      this.#emitTransportEvent('stop_recovery_timeout')
      this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.relayClosedMidRecording))
    }, STOP_RECOVERY_BUDGET_MS)
  }

  #sendStopIfReady(): void {
    if (
      this.#stopRequested &&
      !this.#stopSent &&
      this.#ready &&
      // The relay must receive the whole recording BEFORE the stop, or it
      // finalizes on a partial stream — so a stop waits out the replay pump.
      !this.#replaying &&
      this.#ws &&
      this.#ws.readyState === WS_OPEN
    ) {
      this.#ws.send(
        JSON.stringify({
          type: 'stop',
          ...(this.#stopOptions.contextBefore
            ? { contextBefore: this.#stopOptions.contextBefore }
            : {}),
          ...(this.#stopOptions.contextAfter
            ? { contextAfter: this.#stopOptions.contextAfter }
            : {}),
          ...(this.#stopOptions.dictionaryIds?.length
            ? { dictionaryIds: this.#stopOptions.dictionaryIds }
            : {}),
          ...(this.#stopOptions.appName ? { appName: this.#stopOptions.appName } : {}),
          ...(this.#stopOptions.language ? { language: this.#stopOptions.language } : {}),
          ...(this.#stopOptions.enhancementMode
            ? { enhancementMode: this.#stopOptions.enhancementMode }
            : {}),
        })
      )
      this.#stopSent = true
      this.#stopSentElapsedMs = this.#elapsedMs()
      this.#emitTransportEvent('stop_sent')
      // The stop reached a live connection, so the recovery budget has done its
      // job — from here the ack tiers (or the outer fuse) govern the wait.
      this.#clearStopRecoveryTimer()
      if (this.#stopAckSupported) {
        this.#ackDelayTimer = setTimeout(() => {
          this.#ackDelayTimer = null
          this.#emitTransportEvent('stop_ack_delayed')
        }, STOP_ACK_DELAY_MS)
        // Ack-governed bail, on two conditions. The midstreamReconnect gate keeps
        // callers that do not opt in on the established 30s-only contract. The
        // backlog gate is the load-bearing one: the stop frame queues behind
        // whatever PCM the socket has not flushed, so on a backed-up uplink an
        // unanswered 3s window means "still in the send queue", not "the relay is
        // wedged" — and killing the dictation there would be a false positive on
        // exactly the slow connections that most need the realtime path.
        this.#sampleBufferedAmount(this.#ws)
        if (this.#midstreamReconnect && this.#bufferedAmount <= STOP_ACK_BAIL_MAX_BUFFERED_BYTES) {
          this.#stopAckBailTimer = setTimeout(() => {
            this.#stopAckBailTimer = null
            this.#emitTransportEvent('stop_ack_timeout')
            this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.stopAckTimeout))
          }, STOP_ACK_BAIL_MS)
        }
      }
    }
  }

  abort(): void {
    // Terminal teardown (Escape-cancel, replacing an in-progress session, unmount).
    // Route through #fail so the socket 'close' it triggers is NOT mistaken for a
    // transport failure and retried — otherwise an intentional abort would schedule
    // a fresh WebSocket and send a new `start` after the caller cleared its session
    // ref, leaking an idle relay + upstream STT stream instead of cancelling.
    if (!this.#failure) this.#fail(new Error('voice relay aborted'))
  }

  #onMessage(event: WebSocketEventLike): void {
    this.#lastInboundElapsedMs = this.#elapsedMs()
    let message: {
      type?: string
      text?: string
      message?: string
      code?: unknown
      capabilities?: unknown
      maxDurationSecs?: unknown
      feedbackDiagnostics?: FeedbackDiagnosticsV1
      transcriptionId?: unknown
    }
    try {
      const raw = typeof event.data === 'string' ? event.data : String(event.data)
      message = JSON.parse(raw)
    } catch {
      this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.invalidMessage))
      return
    }

    switch (message.type) {
      case 'ready':
        this.#ready = true
        this.#stopAckSupported =
          Array.isArray(message.capabilities) && message.capabilities.includes('stop_ack_v1')
        this.#readyElapsedMs = this.#elapsedMs()
        if (this.#readyTimer) {
          clearTimeout(this.#readyTimer)
          this.#readyTimer = null
        }
        if (this.#reconnecting) {
          // A REPLACEMENT connection. start() resolved long ago; what this needs
          // is the recording so far, replayed onto the new upstream STT stream
          // before anything live (or the stop) goes out. #pending is dropped
          // because the frame log is the complete record — flushing both would
          // send the tail twice.
          this.#reconnecting = false
          this.#clearMidstreamReadyTimer()
          // This outage is over; a later, unrelated blip starts its backoff from
          // the top rather than inheriting this one's escalation.
          this.#midstreamCycleAttempts = 0
          this.#pending = []
          // Armed BEFORE the telemetry callback runs: onTransportEvent is invoked
          // synchronously, and a handler that reaches pushFrame would otherwise
          // find the session ready-and-open and send a live frame ahead of the
          // backlog it belongs behind. The pump itself runs after the event, so
          // a one-frame replay cannot report `replay_completed` before the
          // `midstream_reconnect_ready` that precedes it.
          this.#armReplay()
          this.#emitTransportEvent('midstream_reconnect_ready')
          this.#pumpReplay()
          break
        }
        this.#flushPending()
        this.#startResolve?.()
        this.#startResolve = null
        this.#startReject = null
        this.#emitTransportEvent('ready')
        this.#sendStopIfReady() // flush a stop() that arrived before ready
        break
      case 'partial':
        this.#lastPartialElapsedMs = this.#elapsedMs()
        this.#options.onPartial?.(message.text ?? '')
        break
      case 'capped': {
        if (this.#capped) break
        this.#capped = true
        this.#emitTransportEvent('cap_reached')
        this.#options.onCapReached?.(
          typeof message.maxDurationSecs === 'number' ? message.maxDurationSecs : undefined
        )
        break
      }
      case 'stop_ack':
        if (!this.#stopSent || this.#stopAckElapsedMs !== undefined) break
        this.#stopAckElapsedMs = this.#elapsedMs()
        this.#clearAckDelayTimer()
        this.#clearStopAckBailTimer()
        this.#emitTransportEvent('stop_ack_received')
        // The relay HAS the stop, so bound the finalize itself — except on a
        // session that survived a reconnect, whose upstream just ingested a
        // replayed backlog and can legitimately need longer. That one keeps only
        // the outer 30s fuse.
        if (this.#midstreamReconnect && !this.#everReconnected) {
          this.#ackedFinalizeTimer = setTimeout(() => {
            this.#ackedFinalizeTimer = null
            this.#emitTransportEvent('final_timeout')
            this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.stopTimeout))
          }, ACKED_FINALIZE_TIMEOUT_MS)
        }
        break
      case 'final': {
        // Marks the session normally complete so the `close(1000)` this triggers
        // is not read as a mid-stream drop. Deliberately NOT done by clearing
        // #ws: that would make the close fail the listener identity guard and the
        // `socket_closed` telemetry the forensic runbook relies on would vanish.
        this.#completed = true
        if (this.#stopTimer) {
          clearTimeout(this.#stopTimer)
          this.#stopTimer = null
        }
        this.#clearAckDelayTimer()
        this.#clearStopAckBailTimer()
        this.#clearAckedFinalizeTimer()
        this.#clearStopRecoveryTimer()
        this.#clearMidstreamTimer()
        this.#clearMidstreamReadyTimer()
        this.#clearReplayTimer()
        this.#replaying = false
        this.#finalElapsedMs = this.#elapsedMs()
        this.#emitTransportEvent('final_received')
        // Empty final = no speech: surface it as 'noise' so the caller runs the
        // same no-speech path the batch transcription returns, instead of pasting
        // an empty string.
        const text = (message.text ?? '').trim()
        const surveyRow: SurveyTranscriptionRef =
          typeof message.transcriptionId === 'number' &&
          Number.isSafeInteger(message.transcriptionId) &&
          message.transcriptionId > 0
            ? { transcriptionId: message.transcriptionId }
            : {}
        this.#stopResolve?.(
          text
            ? {
                status: 'ok',
                text,
                ...surveyRow,
                ...(message.feedbackDiagnostics?.finalTextSource === 'response' &&
                message.text !== text
                  ? { feedbackFinalText: message.text! }
                  : {}),
                ...(message.feedbackDiagnostics
                  ? { feedbackDiagnostics: message.feedbackDiagnostics }
                  : {}),
              }
            : { status: 'noise', text: '', ...surveyRow }
        )
        this.#stopResolve = null
        this.#stopReject = null
        this.#ws?.close(1000, 'completed')
        break
      }
      case 'error':
        this.#fail(
          new VoiceRelayError(
            message.message ?? 'voice relay error',
            typeof message.code === 'string' ? message.code : null
          )
        )
        break
      default:
        break
    }
  }

  #flushPending(): void {
    if (!this.#ws) return
    for (const frame of this.#pending) this.#sendAudioFrame(this.#ws, frame)
    this.#pending = []
  }

  #onClose(event: WebSocketEventLike): void {
    this.#closeCode = event.code
    this.#closeReason = sanitizeCloseReason(event.reason)
    this.#closeWasClean = event.wasClean
    // Emitted for EVERY close, the normal `close(1000, 'completed')` included —
    // the forensic runbook pairs it with `final_received` to tell an orderly
    // teardown apart from a drop, so it must not become conditional.
    this.#emitTransportEvent('socket_closed')
    // The session already finished normally; this is its own goodbye.
    if (this.#completed || this.#failure) return
    // A failed mid-stream reconnect attempt: back off and try the next one.
    if (this.#reconnecting) {
      this.#onMidstreamAttemptFailure()
      return
    }
    // A close BEFORE ready with an in-flight start is a failed connect — retry it.
    // (After ready the start has resolved, so this guard is false.)
    if (!this.#ready && this.#startReject) {
      this.#onConnectFailure(new Error(VOICE_STREAMING_FAILURE_MESSAGES.closedBeforeCompleting))
      return
    }
    // A post-ready drop the frame log can still recover from. This includes a
    // drop AFTER the stop went out: the stop is re-sent on the fresh connection
    // once the replay catches up — but only if all of that fits inside
    // STOP_RECOVERY_BUDGET_MS, since a stopped user is waiting and batch is
    // usually the faster answer past that point.
    if (this.#canMidstreamReconnect()) {
      this.#beginMidstreamReconnect()
      return
    }
    // A close while a promise is in flight — terminal, so the caller falls back
    // to batch (which holds the full audio).
    if (this.#startReject || this.#stopReject) {
      this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.closedBeforeCompleting))
      return
    }
    // Post-ready with nothing in flight: the user is still speaking into a socket
    // that is gone, and no promise exists to carry the error. Terminal here, so
    // the caller reaches batch at the moment of the tap-off rather than after the
    // 30s fuse expires waiting for a final that cannot arrive.
    if (this.#ready) {
      this.#fail(new Error(VOICE_STREAMING_FAILURE_MESSAGES.relayClosedMidRecording))
    }
  }

  #canMidstreamReconnect(): boolean {
    return (
      this.#midstreamReconnect &&
      this.#ready &&
      !this.#failure &&
      !this.#completed &&
      !this.#frameLogOverflowed
    )
  }

  // Enter recovery: tear down the dead socket and schedule a replacement. The
  // recording keeps running throughout — pushFrame() goes on filling the frame
  // log, which is what the new connection will be brought up to date with.
  #beginMidstreamReconnect(): void {
    if (this.#reconnecting || this.#failure) return
    this.#reconnecting = true
    this.#everReconnected = true
    // The replacement connection re-handshakes from scratch: it has its own
    // `ready`, and renegotiates `stop_ack_v1` from ITS capabilities rather than
    // inheriting the dead connection's.
    this.#ready = false
    this.#stopAckSupported = false
    this.#replaying = false
    this.#clearReplayTimer()
    this.#clearAckDelayTimer()
    this.#clearStopAckBailTimer()
    this.#clearAckedFinalizeTimer()
    // A stop that already went out died with the socket, so the new connection
    // has to be told again once it is caught up.
    this.#stopSent = false
    this.#stopAckElapsedMs = undefined
    this.#pending = []
    // Drop the dead socket BEFORE closing it, so the close() this triggers fails
    // the listener identity guard instead of re-entering the state machine.
    const dead = this.#ws
    this.#ws = null
    dead?.close()
    // Covers the "stop was already sent, THEN the socket died" ordering: the user
    // is waiting, so the recovery is on the clock from right now.
    this.#armStopRecoveryBudget()
    this.#scheduleMidstreamAttempt()
  }

  #scheduleMidstreamAttempt(): void {
    const delay =
      MIDSTREAM_RECONNECT_BACKOFF_MS[this.#midstreamCycleAttempts] ??
      MIDSTREAM_RECONNECT_MAX_BACKOFF_MS
    this.#midstreamCycleAttempts += 1
    this.#midstreamAttempts += 1
    this.#midstreamTimer = setTimeout(() => {
      this.#midstreamTimer = null
      // abort()/a stop-budget expiry may have ended the session while we waited.
      if (this.#failure || !this.#reconnecting) return
      this.#emitTransportEvent('midstream_reconnect_attempt')
      // The reconnect loop is driven by error/close, which a half-open socket
      // never delivers — so every attempt carries its own deadline to reach
      // `ready`, or the session would sit in `reconnecting` forever.
      this.#midstreamReadyTimer = setTimeout(() => {
        this.#midstreamReadyTimer = null
        this.#onMidstreamAttemptFailure()
      }, MIDSTREAM_ATTEMPT_READY_TIMEOUT_MS)
      this.#createAndBindSocket()
    }, delay)
  }

  #onMidstreamAttemptFailure(): void {
    if (this.#failure || !this.#reconnecting) return
    this.#clearMidstreamReadyTimer()
    const dead = this.#ws
    this.#ws = null
    dead?.close()
    this.#scheduleMidstreamAttempt()
  }

  // Claim the send path for the replay pump. From here until #finishReplay,
  // pushFrame only appends to the log, so the whole recording reaches the fresh
  // connection from frame 0 — in the same order the dead one saw it — before any
  // live frame or the stop goes out.
  #armReplay(): void {
    this.#replaying = true
    this.#replayIndex = 0
    this.#replayStartedAtMs = performance.now()
    this.#replayEmittedMs = 0
    this.#replayPausedMs = 0
    this.#replayPauseStartedAtMs = null
  }

  // Sends everything the wall clock says is due, not one frame per wakeup. The
  // distinction is load-bearing rather than cosmetic: with the recording window
  // hidden, macOS clamps this timer to ~1 s, so a one-frame-per-tick pump drains
  // at ~1 frame/s — measured on a real device as 36 frames (3.6 s of audio) taking
  // 26 s, against the 20 frames/s REPLAY_PACE calls for. Catching up by the clock
  // makes the replay rate independent of timer precision: a tick that arrives 1 s
  // late sends the ~20 frames that second was owed.
  #pumpReplay(): void {
    this.#replayTimer = null
    if (!this.#replaying || this.#failure) return
    const socket = this.#ws
    // The socket died mid-replay; its close/error handler owns what happens next.
    if (!socket || socket.readyState !== WS_OPEN) return

    const now = performance.now()
    if (this.#replayPauseStartedAtMs !== null) {
      this.#replayPausedMs += Math.max(0, now - this.#replayPauseStartedAtMs)
      this.#replayPauseStartedAtMs = null
    }
    const elapsedMs = Math.max(0, now - this.#replayStartedAtMs - this.#replayPausedMs)

    while (this.#replayEmittedMs <= elapsedMs) {
      // Caught up with the tail, including frames pushed DURING the replay.
      const frame = this.#frameLog[this.#replayIndex]
      if (frame === undefined) {
        this.#finishReplay()
        return
      }

      this.#sampleBufferedAmount(socket)
      // Above the backlog ceiling, idle a fixed beat instead of sending — a slow
      // uplink must not be handed the whole log at once. This is also what bounds
      // a single catch-up burst: each frame re-checks the socket, so a wakeup that
      // arrives arbitrarily late stops at the ceiling rather than flushing the
      // whole log. The frame's own duration is the wrong delay here: it was not
      // sent, so it says nothing about when the socket will have drained.
      if (this.#bufferedAmount > MAX_REPLAY_BUFFERED_BYTES) {
        this.#replayPauseStartedAtMs = now
        this.#replayTimer = setTimeout(() => this.#pumpReplay(), REPLAY_BACKPRESSURE_IDLE_MS)
        return
      }

      this.#sendAudioFrame(socket, frame)
      this.#replayedBytes += frame.byteLength
      this.#replayIndex += 1
      this.#replayEmittedMs += this.#replayIntervalMs(frame)
    }

    if (this.#replayIndex >= this.#frameLog.length) {
      this.#finishReplay()
      return
    }
    // Ask for the moment the next frame comes due. The clamp may well ignore it;
    // the catch-up loop above is what makes that harmless.
    this.#replayTimer = setTimeout(
      () => this.#pumpReplay(),
      Math.max(0, Math.round(this.#replayEmittedMs - elapsedMs))
    )
  }

  // Wall-clock duration of a frame, divided by REPLAY_PACE: the log is drained
  // at that multiple of realtime regardless of how the capture chunks it.
  #replayIntervalMs(frame: Uint8Array | undefined): number {
    if (!frame) return 0
    const sampleRate = Math.max(1, this.#options.sampleRate)
    const frameMs = (frame.byteLength / (sampleRate * BYTES_PER_SAMPLE)) * 1_000
    return Math.max(0, Math.round(frameMs / REPLAY_PACE))
  }

  #finishReplay(): void {
    this.#clearReplayTimer()
    this.#replaying = false
    this.#emitTransportEvent('replay_completed')
    // The relay now has everything; a stop that was waiting on the replay can go.
    this.#sendStopIfReady()
  }

  // A transport-level connect failure (open threw, socket errored, or it closed
  // before `ready`). Retry the connect while attempts remain; otherwise fail so the
  // caller falls back to batch. Buffered PCM + the pending start/stop survive
  // because they live on the instance, not the socket.
  #onConnectFailure(error: Error): void {
    if (this.#connectAttempts < this.#maxConnectAttempts && !this.#failure) {
      // Drop the dead socket BEFORE closing it: close() may synchronously emit
      // 'close', and nulling first makes that event fail the identity guard so it
      // can't re-enter this handler. (A real WebSocket fires 'error' THEN 'close'
      // for one failure — both must be ignored once we've moved on.)
      const dead = this.#ws
      this.#ws = null
      dead?.close()
      this.#reconnectTimer = setTimeout(() => {
        this.#reconnectTimer = null
        // A stop()/abort() may have failed the session, or a later socket may have
        // already reached ready, while we waited — don't open a zombie connection.
        if (this.#failure || this.#ready) return
        // Report only attempts we actually initiate (an abort during the delay
        // cancels this one), so the telemetry counts true reconnects, not intents.
        this.#options.onReconnectAttempt?.(this.#connectAttempts + 1, error)
        this.#openSocket()
      }, this.#reconnectDelayMs)
      return
    }
    this.#fail(error)
  }

  #fail(error: Error): void {
    this.#failure = error
    if (this.#stopTimer) {
      clearTimeout(this.#stopTimer)
      this.#stopTimer = null
    }
    this.#clearAckDelayTimer()
    if (this.#readyTimer) {
      clearTimeout(this.#readyTimer)
      this.#readyTimer = null
    }
    if (this.#reconnectTimer) {
      clearTimeout(this.#reconnectTimer)
      this.#reconnectTimer = null
    }
    this.#clearMidstreamTimer()
    this.#clearMidstreamReadyTimer()
    this.#clearReplayTimer()
    this.#clearStopRecoveryTimer()
    this.#clearStopAckBailTimer()
    this.#clearAckedFinalizeTimer()
    this.#reconnecting = false
    this.#replaying = false
    // Terminal: nothing will ever be sent again, and the batch fallback reads the
    // complete recording from the local WAV — so hold no audio.
    this.#pending = []
    this.#frameLog = []
    this.#frameLogBytes = 0
    this.#startReject?.(error)
    this.#stopReject?.(error)
    this.#startResolve = null
    this.#startReject = null
    this.#stopResolve = null
    this.#stopReject = null
    // Null AFTER closing so any late 'error'/'close' from this socket (a real
    // WebSocket fires both on a transport failure) fails the listeners' identity
    // guard and can't re-enter the state machine post-termination.
    const dead = this.#ws
    this.#ws = null
    dead?.close()
  }

  #sendAudioFrame(socket: WebSocketLike, frame: Uint8Array): void {
    socket.send(frame)
    this.#framesSent += 1
    this.#bytesSent += frame.byteLength
    this.#sampleBufferedAmount(socket)
  }

  #sampleBufferedAmount(socket: WebSocketLike | null = this.#ws): void {
    const amount = socket?.bufferedAmount
    this.#bufferedAmount =
      typeof amount === 'number' && Number.isFinite(amount) && amount >= 0 ? amount : 0
    this.#maxBufferedAmount = Math.max(this.#maxBufferedAmount, this.#bufferedAmount)
  }

  #elapsedMs(): number {
    return Math.max(0, Date.now() - this.#sessionStartedAtMs)
  }

  #snapshot(): VoiceStreamingTransportSnapshot {
    this.#sampleBufferedAmount()
    return {
      clientSessionId: this.#clientSessionId,
      elapsedMs: this.#elapsedMs(),
      connectAttempt: this.#connectAttempts,
      readyState: this.#ws?.readyState ?? null,
      framesSent: this.#framesSent,
      bytesSent: this.#bytesSent,
      bufferedAmount: this.#bufferedAmount,
      maxBufferedAmount: this.#maxBufferedAmount,
      pendingFrames: this.#pending.length,
      midstreamReconnectAttempts: this.#midstreamAttempts,
      replayedBytes: this.#replayedBytes,
      socketCreatedElapsedMs: this.#socketCreatedElapsedMs,
      socketOpenedElapsedMs: this.#socketOpenedElapsedMs,
      readyElapsedMs: this.#readyElapsedMs,
      lastInboundElapsedMs: this.#lastInboundElapsedMs,
      lastPartialElapsedMs: this.#lastPartialElapsedMs,
      stopSentElapsedMs: this.#stopSentElapsedMs,
      stopAckElapsedMs: this.#stopAckElapsedMs,
      stopAckLatencyMs:
        this.#stopSentElapsedMs !== undefined && this.#stopAckElapsedMs !== undefined
          ? this.#stopAckElapsedMs - this.#stopSentElapsedMs
          : undefined,
      finalElapsedMs: this.#finalElapsedMs,
      closeCode: this.#closeCode,
      closeReason: this.#closeReason,
      closeWasClean: this.#closeWasClean,
    }
  }

  #emitTransportEvent(type: VoiceStreamingTransportEventType): void {
    try {
      this.#options.onTransportEvent?.({
        type,
        occurredAtMs: Date.now(),
        snapshot: this.#snapshot(),
      })
    } catch (error) {
      // Diagnostics must never alter dictation behavior.
      console.warn('voice relay: transport diagnostics callback failed', error)
    }
  }

  #clearAckDelayTimer(): void {
    if (!this.#ackDelayTimer) return
    clearTimeout(this.#ackDelayTimer)
    this.#ackDelayTimer = null
  }

  #clearMidstreamTimer(): void {
    if (!this.#midstreamTimer) return
    clearTimeout(this.#midstreamTimer)
    this.#midstreamTimer = null
  }

  #clearMidstreamReadyTimer(): void {
    if (!this.#midstreamReadyTimer) return
    clearTimeout(this.#midstreamReadyTimer)
    this.#midstreamReadyTimer = null
  }

  #clearReplayTimer(): void {
    if (!this.#replayTimer) return
    clearTimeout(this.#replayTimer)
    this.#replayTimer = null
  }

  #clearStopRecoveryTimer(): void {
    if (!this.#stopRecoveryTimer) return
    clearTimeout(this.#stopRecoveryTimer)
    this.#stopRecoveryTimer = null
  }

  #clearStopAckBailTimer(): void {
    if (!this.#stopAckBailTimer) return
    clearTimeout(this.#stopAckBailTimer)
    this.#stopAckBailTimer = null
  }

  #clearAckedFinalizeTimer(): void {
    if (!this.#ackedFinalizeTimer) return
    clearTimeout(this.#ackedFinalizeTimer)
    this.#ackedFinalizeTimer = null
  }
}

function resolveClientSessionId(requested?: string): string {
  if (requested && CLIENT_SESSION_ID_PATTERN.test(requested)) return requested.toLowerCase()
  return globalThis.crypto.randomUUID()
}

function addClientSessionIdToRelayUrl(relayUrl: string, clientSessionId: string): string {
  const url = new URL(relayUrl)
  url.searchParams.set('client_session_id', clientSessionId)
  return url.toString()
}

function sanitizeCloseReason(reason?: string): string | undefined {
  if (!reason) return undefined
  const trimmed = reason.trim().slice(0, 120)
  return /^[A-Za-z0-9 _.:/-]+$/.test(trimmed) ? trimmed : 'redacted_non_ascii'
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}
